// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, InventoryAvailabilityDto, ProductInventoryDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import {
    BomLineModel,
    ConfigureQtoModel,
    ConfigureResponseModel,
    ConfigureSuccessModel,
} from "@store/Framework/Types/ValveConfiguratorApiModels";
import Block from "@store/Library/Components/Toolkit/Block";
import Button from "@insite/mobius/Button";
import AddToListModal from "@insite/content-library/Components/AddToListModal";
import getColor from "@insite/mobius/utilities/getColor";
import Tooltip from "@insite/mobius/Tooltip";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useBomPricing, BomPriceInfo } from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/useBomPricing";
import { useBomLeadTime } from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/useBomLeadTime";
import ValveConfiguratorQuoteForm from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/ValveConfiguratorQuoteForm";
import UnitHeaderCard from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/UnitHeaderCard";
import Icon from "@insite/mobius/Icon";
import AlertCircleFill, {
    AlertCircleFillOffset,
} from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleFill";
import AlertTriangleFill, {
    AlertTriangleFillOffset,
} from "@store/Library/Components/Toolkit/Icons/AlertTriangle/AlertTriangleFill";
import CheckmarkCircle2Fill, {
    CheckmarkCircle2FillOffset,
} from "@store/Library/Components/Toolkit/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import { StatusColor, statusStyles } from "@store/Themes/common/mixins/status";
import InfoLine from "@store/Library/Components/Toolkit/Icons/Info/InfoLine";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { useSummaryProduct } from "@store/Library/Widgets/ProductConfiguration/ValveConfigurator/ValveConfiguratorSummaryPurchase";
import React, { FC, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import styled, { css, keyframes } from "styled-components";

const spin = keyframes`
    to {
        transform: rotate(360deg);
    }
`;

// Brand-aligned palette — every entry resolves to a token from the store theme.
const FLS = {
    cta: getColor("primary"),
    ctaHover: getColor("primary.dark"),
    ink: getColor("text.main"),
    text: getColor("text.main"),
    muted: getColor("text.accent"),
    border: getColor("common.border"),
    background: getColor("common.background"),
    summary: getColor("primary.light"),
    rowAlt: getColor("common.borderLight"),
    accent: getColor("common.accent"),
    warn: getColor("warning.main"),
    warnBg: getColor("warning.light"),
    warnBorder: getColor("warning.main"),
};

const mapStateToProps = (state: ApplicationState) => {
    const v = state.store.data.valveConfigurator;
    return {
        configurationResult: v.configurationResult,
        hasSubmitted: v.hasSubmitted,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(16)};
    color: ${FLS.text};
`;

const SummaryCard = styled.section`
    background: ${FLS.summary};
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(6)};
    padding: ${pxToRem(16)} ${pxToRem(20)};
`;

const SummaryHeading = styled.h3`
    margin: 0 0 ${pxToRem(12)} 0;
    font-size: ${pxToRem(13)};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${FLS.ink};
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${pxToRem(16)};

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
        gap: ${pxToRem(8)};
    }
`;

const SummaryCell = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(2)};

    @media (max-width: 600px) {
        flex-direction: row;
        align-items: baseline;
        justify-content: space-between;
        gap: ${pxToRem(12)};
    }
`;

const SummaryLabel = styled.span`
    font-size: ${pxToRem(11)};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${FLS.muted};

    @media (max-width: 600px) {
        font-size: ${pxToRem(13)};
        text-transform: none;
        letter-spacing: 0;
    }
`;

const SummaryValue = styled.span`
    font-size: ${pxToRem(15)};
    font-weight: 500;
    color: ${FLS.ink};
    font-variant-numeric: tabular-nums;

    @media (max-width: 600px) {
        font-weight: 700;
        text-align: right;
    }
`;

const AutolubricationNote = styled.div`
    border: 1px solid ${FLS.warnBorder};
    background: ${FLS.warnBg};
    border-radius: ${pxToRem(6)};
    padding: ${pxToRem(12)} ${pxToRem(16)};
    color: ${FLS.warn};
    font-size: ${pxToRem(13)};
    line-height: 1.45;

    strong {
        font-weight: 600;
        margin-right: ${pxToRem(6)};
    }
`;

const BomCard = styled.section`
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(6)};
    background: ${FLS.background};
    padding: ${pxToRem(16)} ${pxToRem(20)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    @media (max-width: 600px) {
        padding: ${pxToRem(12)};
    }
`;

const BomHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: ${pxToRem(12)};
    margin-bottom: ${pxToRem(12)};
`;

const BomHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: ${pxToRem(16)};
`;

const BomTitle = styled.h3`
    margin: 0;
    font-size: ${pxToRem(15)};
    font-weight: 600;
    color: ${FLS.ink};
`;

const PricingStatus = styled.span`
    font-size: ${pxToRem(11)};
    color: ${FLS.muted};
    display: inline-flex;
    align-items: center;
    gap: ${pxToRem(6)};
`;

const Spinner = styled.span`
    width: ${pxToRem(10)};
    height: ${pxToRem(10)};
    border: 2px solid ${FLS.border};
    border-top-color: ${FLS.cta};
    border-radius: 50%;
    display: inline-block;
    animation: ${spin} 0.8s linear infinite;
`;

// Shared column template so the header labels line up with the row cells at every
// breakpoint. Both the header and each row are grids over the same named areas.
const bomRowGrid = css`
    display: grid;
    align-items: center;
    column-gap: ${pxToRem(16)};
    row-gap: ${pxToRem(8)};
    grid-template-columns: ${pxToRem(48)} minmax(0, 1fr) ${pxToRem(210)} auto;
    grid-template-areas: "thumb meta stock prices";

    @media (max-width: 991px) {
        grid-template-columns: ${pxToRem(48)} minmax(0, 1fr) auto;
        grid-template-areas:
            "thumb meta prices"
            ". stock stock";
    }

    @media (max-width: 600px) {
        grid-template-columns: ${pxToRem(48)} minmax(0, 1fr);
        grid-template-areas:
            "thumb meta"
            "stock stock"
            "prices prices";
    }
`;

const BomList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(8)};
`;

const BomItem = styled.li`
    ${bomRowGrid}
    padding: ${pxToRem(12)};
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(6)};
    background: ${FLS.background};
    box-shadow: 0 1px 0 rgba(0, 0, 0, 0.02);
`;

const Thumb = styled.div`
    grid-area: thumb;
    align-self: start;
    width: ${pxToRem(48)};
    height: ${pxToRem(48)};
    border-radius: ${pxToRem(4)};
    background: ${FLS.accent};
    border: 1px solid ${FLS.border};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        background: #fff;
    }
`;

const ItemMeta = styled.div`
    grid-area: meta;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(2)};
`;

const PartNumber = styled.span`
    font-size: ${pxToRem(13)};
    font-weight: 600;
    color: ${FLS.ink};
    display: inline-flex;
    align-items: center;
    gap: ${pxToRem(8)};
    font-variant-numeric: tabular-nums;
`;

const PartNumberLink = styled.a`
    color: inherit;
    text-decoration: none;
    &:hover,
    &:focus {
        text-decoration: underline;
        color: ${FLS.cta};
    }
`;

const Description = styled.span`
    font-size: ${pxToRem(12)};
    color: ${FLS.muted};
    line-height: 1.4;
`;

const QtoBadge = styled.span`
    font-size: ${pxToRem(10)};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${FLS.warn};
    background: ${FLS.warnBg};
    border: 1px solid ${FLS.warnBorder};
    padding: ${pxToRem(2)} ${pxToRem(6)};
    border-radius: ${pxToRem(10)};
`;

const PriceCols = styled.div`
    grid-area: prices;
    display: flex;
    gap: ${pxToRem(20)};
    font-variant-numeric: tabular-nums;

    @media (max-width: 600px) {
        width: 100%;
        justify-content: space-between;
        gap: ${pxToRem(12)};
        padding-top: ${pxToRem(4)};
        border-top: 1px solid ${FLS.border};
    }
`;

const PriceCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(2)};
    min-width: ${pxToRem(72)};
    text-align: right;

    @media (max-width: 600px) {
        min-width: 0;
        flex: 1 1 0;
    }
`;

const PriceLabel = styled.span`
    font-size: ${pxToRem(10)};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${FLS.muted};

    /* Redundant while the sticky column header is visible. */
    @media (min-width: 992px) {
        display: none;
    }
`;

const PriceValue = styled.span<{ $strong?: boolean }>`
    font-size: ${pxToRem(13)};
    color: ${({ $strong }) => ($strong ? FLS.ink : FLS.text)};
    font-weight: ${({ $strong }) => ($strong ? 600 : 400)};
`;

const SuggestedPrice = styled.span`
    display: block;
    font-size: ${pxToRem(11)};
    color: ${FLS.muted};
    text-decoration: line-through;
`;

const ColumnLabels = styled.div`
    ${bomRowGrid}
    padding: ${pxToRem(10)} ${pxToRem(12)};
    margin-bottom: ${pxToRem(8)};
    background: ${FLS.rowAlt};
    border-radius: ${pxToRem(4)};
    font-size: ${pxToRem(12)};
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${FLS.muted};

    /* Row-level labels are repeated inside each row on small screens. */
    @media (max-width: 991px) {
        display: none;
    }
`;

const ColPartLabel = styled.span`
    grid-area: meta;
    min-width: 0;
`;

const ColStockLabel = styled.span`
    grid-area: stock;
    text-align: left;
`;

const ColRightGroup = styled.span`
    grid-area: prices;
    display: flex;
    gap: ${pxToRem(20)};

    > span {
        text-align: right;
        min-width: ${pxToRem(72)};
    }
`;

const StockCell = styled.div`
    grid-area: stock;
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(4)};
    min-width: 0;
`;

// Same info glyph the ProductAvailability override uses for its tooltips.
const tooltipProps = {
    iconProps: {
        src: InfoLine,
        color: "swatch.black",
    },
} as const;

// Matches the ProductAvailability override so BOM rows read the same as PLP/PDP.
const StockInline = styled.div<{ _color: StatusColor; _iconOffset?: IconOffset }>`
    ${x => statusStyles(x._color, x._iconOffset)}
    border-radius: ${pxToRem(28)};
    width: fit-content;
    white-space: nowrap;
`;

const StockInlineRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${pxToRem(6)};
`;

const stockPresentation: Record<
    "success" | "warning" | "danger" | "neutral",
    { color: StatusColor; iconSrc: React.ComponentType<any>; iconOffset: IconOffset }
> = {
    success: { color: "success", iconSrc: CheckmarkCircle2Fill, iconOffset: CheckmarkCircle2FillOffset },
    warning: { color: "warning", iconSrc: AlertTriangleFill, iconOffset: AlertTriangleFillOffset },
    danger: { color: "danger", iconSrc: AlertTriangleFill, iconOffset: AlertTriangleFillOffset },
    neutral: { color: "neutral", iconSrc: AlertCircleFill, iconOffset: AlertCircleFillOffset },
};

const ShipDateLine = styled.div`
    display: inline-flex;
    align-items: center;
    gap: ${pxToRem(6)};
    font-size: ${pxToRem(12)};
    color: ${FLS.muted};
    white-space: nowrap;
`;

const BomFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: baseline;
    flex-wrap: wrap;
    gap: ${pxToRem(24)};
    padding: ${pxToRem(14)} ${pxToRem(12)} ${pxToRem(4)};
    border-top: 1px solid ${FLS.border};
    margin-top: ${pxToRem(4)};

    @media (max-width: 600px) {
        justify-content: space-between;
        gap: ${pxToRem(8)};
    }
`;

const BomFooterLabel = styled.span`
    font-size: ${pxToRem(12)};
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${FLS.muted};
`;

const BomFooterValue = styled.span`
    font-size: ${pxToRem(20)};
    font-weight: 700;
    color: ${FLS.ink};
    font-variant-numeric: tabular-nums;
`;

const QuoteFormSection = styled.div`
    /* Scroll target for the Request-a-Quote CTA. */
    scroll-margin-top: ${pxToRem(24)};
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(6)};
    background: ${FLS.background};
    padding: ${pxToRem(16)} ${pxToRem(20)};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    @media (max-width: 600px) {
        padding: ${pxToRem(12)};
    }
`;

// Sticky-within-results action bar shown only on mobile (<= 991px). Uses
// position: sticky (not fixed) so it pins to the viewport bottom while the
// BOM/QTO panel is in view and scrolls away once the user passes the bottom
// of the parent (Stack for success / QtoPanel for QTO).
const MobileStickyActions = styled.div`
    display: none;

    @media (max-width: 991px) {
        display: flex;
        flex-direction: column;
        gap: ${pxToRem(8)};
        position: sticky;
        bottom: 0;
        z-index: 5;
        background: #fff;
        border-top: 1px solid ${FLS.border};
        padding: ${pxToRem(12)} ${pxToRem(16)};
        padding-bottom: max(${pxToRem(12)}, env(safe-area-inset-bottom));
        box-shadow: 0 -4px 12px -8px rgba(0, 0, 0, 0.15);

        > button {
            width: 100%;
        }
    }
`;

// QTO variant: negative side/bottom margins to break out of QtoPanel padding so
// the sticky bar still spans the panel edge-to-edge.
const MobileStickyActionsInPanel = styled(MobileStickyActions)`
    @media (max-width: 991px) {
        margin: ${pxToRem(12)} ${pxToRem(-24)} ${pxToRem(-20)};
    }
`;

const ModifyLinkButton = styled.button`
    background: none;
    border: 0;
    padding: ${pxToRem(4)} 0;
    color: ${FLS.cta};
    font-size: ${pxToRem(13)};
    font-weight: 600;
    cursor: pointer;
    text-align: center;

    &:hover {
        text-decoration: underline;
    }
`;

// Inline "Modify configurator" link that appears at the top of the results on mobile.
const MobileModifyTopLink = styled(ModifyLinkButton)`
    display: none;

    @media (max-width: 991px) {
        display: inline-flex;
        align-items: center;
        gap: ${pxToRem(6)};
    }
`;

const QtoPanel = styled.section`
    border: 1px solid ${FLS.warnBorder};
    background: ${FLS.warnBg};
    border-radius: ${pxToRem(6)};
    padding: ${pxToRem(20)} ${pxToRem(24)};
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(12)};
`;

const QtoHeader = styled.div`
    display: flex;
    align-items: center;
    gap: ${pxToRem(10)};
    color: ${FLS.warn};
    font-size: ${pxToRem(15)};
    font-weight: 700;
`;

const QtoMessage = styled.p`
    margin: 0;
    color: ${FLS.text};
    font-size: ${pxToRem(13)};
`;

const QtoLeadTime = styled.p`
    margin: 0;
    color: ${FLS.ink};
    font-size: ${pxToRem(13)};
    font-weight: 600;
`;

const QtoFootnote = styled.span`
    font-size: ${pxToRem(12)};
    color: ${FLS.muted};
`;

function isContactInsideSales(line: BomLineModel): boolean {
    return /contact inside sales/i.test(line.partNumber);
}

/** Extract the availability entry for the requested UOM (default EA), returning tone / label / raw msg. Returns undefined when inventory hasn't loaded yet. */
function resolveStockFor(inventory: ProductInventoryDto | undefined):
    | { tone: "success" | "warning" | "danger" | "neutral"; label: string }
    | undefined {
    if (!inventory) {
        return undefined;
    }
    // BE-driven suppression flag: matches the ProductAvailability override.
    if (inventory.additionalResults?.hideStockStatus === "true") {
        return undefined;
    }
    const availability = inventory.inventoryAvailabilityDtos?.find(
        (d: InventoryAvailabilityDto) => (d.unitOfMeasure || "").toLowerCase() === "ea" || !d.unitOfMeasure,
    )?.availability;
    if (!availability) {
        return undefined;
    }
    if (availability.messageType === AvailabilityMessageType.OutOfStock) {
        return { tone: "danger", label: availability.message || "Extended Delivery Time" };
    }
    if (availability.messageType === AvailabilityMessageType.LowStock) {
        return { tone: "warning", label: availability.message || "Low stock" };
    }
    // NoMessage means the ERP gave us nothing usable; render it muted rather than as good news.
    if (availability.messageType === AvailabilityMessageType.NoMessage) {
        return { tone: "neutral", label: availability.message || "Inventory status unknown" };
    }
    return { tone: "success", label: availability.message || "In stock" };
}

function formatMoney(value: number, currency = "USD"): string {
    return value.toLocaleString(undefined, { style: "currency", currency });
}

// Insite serialises additionalResults with camelCase keys; mirrors the precedence
// used by the ProductAvailability override.
function resolveShipDateFor(inventory: ProductInventoryDto | undefined): string | undefined {
    const extra = inventory?.additionalResults;
    return extra?.estimatedFormattedShippingDate || extra?.leadTimeDisplay || undefined;
}

function SuccessView({ r, onModify }: { r: ConfigureSuccessModel; onModify: () => void }) {
    const partNumbers = useMemo(
        () => r.bom.map(b => b.partNumber).filter(pn => !!pn && !/contact inside sales/i.test(pn)),
        [r.bom],
    );
    const pricing = useBomPricing(partNumbers, r.partNumber);
    const { extensionCatalogSettings } = useSettingsCollection();
    const notFoundImage = extensionCatalogSettings?.notFoundSmallImagePath;

    const [unitQty, setUnitQty] = useState(1);
    const quoteFormRef = useRef<HTMLDivElement>(null);

    // Server-side rules 1-4 already produced the unit's price (Rule 1 direct or Rule 2 BOM sum).
    // Quote-required when the PN did not resolve to a catalog product or ended up unpriced.
    const unitInfo: BomPriceInfo | undefined = pricing.unit;
    const isOrderable = !!(unitInfo?.productId && unitInfo.unitNetPrice > 0);

    // Per-line derived stock info + BOM total, computed once per BOM change.
    const perLine = useMemo(
        () =>
            r.bom.map(line => {
                const key = (line.partNumber || "").trim().toUpperCase();
                const info = pricing.prices.get(key);
                const stock = resolveStockFor(info?.inventory);
                const shipDate = resolveShipDateFor(info?.inventory);
                return { line, info, stock, shipDate };
            }),
        [r.bom, pricing.prices],
    );

    const bomSum = useMemo(
        () => perLine.reduce((sum, x) => sum + (x.info ? x.info.unitNetPrice * x.line.qty : 0), 0),
        [perLine],
    );

    // A sum is only meaningful when every line contributed a price; otherwise we
    // fall back to the same "—" placeholder the individual line prices use.
    const allLinesPriced = perLine.length > 0 && perLine.every(x => !!x.info);

    // Quote-required units have no price of their own, but their BOM lines often do —
    // show that sum as the estimated total instead of $0.
    const currency = unitInfo?.currency || perLine.find(x => x.info?.currency)?.info?.currency || "USD";
    const unitPricePerAssembly = isOrderable ? unitInfo!.unitNetPrice : allLinesPriced ? bomSum : 0;
    const unitTotal = unitPricePerAssembly * unitQty;
    const unitTotalDisplay = formatMoney(unitTotal, currency);
    // The BOM table lists per-assembly line prices, so its footer stays at the
    // single-assembly total and does not track the header's qty stepper.
    const bomTotalDisplay = allLinesPriced ? formatMoney(unitPricePerAssembly, currency) : "—";

    // Unit stock comes from the Unit PN's own real-time inventory result (the same
    // /realtimeinventory call now applies the KREBS Rules 1-3 lead time/BOM fallback
    // server-side for this exact PN) rather than being derived by aggregating the
    // individual BOM line statuses shown in the table below.
    const unitInventoryStock = resolveStockFor(pricing.unit?.inventory);
    const unitStock: "in" | "low" | "out" | "unknown" | undefined = !unitInventoryStock
        ? undefined
        : !isOrderable || unitInventoryStock.tone === "danger"
            ? "out"
            : unitInventoryStock.tone === "warning"
                ? "low"
                : unitInventoryStock.tone === "neutral"
                    ? "unknown"
                    : "in";
    const unitStockLabel = !unitStock
        ? undefined
        : unitStock === "out"
            ? "Extended Delivery Time"
            : unitStock === "low"
                ? "Low stock"
                : unitStock === "unknown"
                    ? "Inventory status unknown"
                    : "In stock";

    // Shipping caption comes from the same real-time inventory result used for the
    // stock badge above (rules 1-3 already applied server-side), so it does not
    // require a separate /bomLeadTime request and does not need to be re-fetched
    // when the qty stepper changes.
    const unitShipDate = resolveShipDateFor(pricing.unit?.inventory);
    const unitShippingCaption = unitShipDate ? `Estimated shipping: ${unitShipDate}` : undefined;

    // Load the unit's ProductModel + real-time price/inventory once at the
    // widget level so we don't duplicate the calls inside UnitHeaderCard and
    // (formerly) MobileStickyActions. Passing the resulting ProductContext down
    // preserves the OOTB Add-to-Cart pipeline via ProductAddToCartButton.
    const summary = useSummaryProduct(isOrderable ? r.partNumber : "");
    const productContext = useMemo<ProductContextModel | undefined>(() => {
        if (!summary.product) {
            return undefined;
        }
        const productInfo: ProductInfo = {
            ...createFromProduct(summary.product),
            qtyOrdered: unitQty,
            unitOfMeasure: summary.unitOfMeasure || "EA",
            pricing: summary.pricing,
            inventory: summary.inventory,
            failedToLoadInventory: summary.inventory ? undefined : true,
        };
        return {
            product: summary.product,
            productInfo,
            onQtyOrderedChanged: (qty: number) => setUnitQty(qty > 0 ? qty : 1),
            onUnitOfMeasureChanged: () => undefined,
        };
    }, [summary.product, summary.pricing, summary.inventory, summary.unitOfMeasure, unitQty]);

    const scrollToQuoteForm = () => {
        quoteFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Stack>
            <MobileModifyTopLink type="button" onClick={onModify} data-test-selector="valveModifyTop">
                <span aria-hidden="true">←</span>
                {translate("FlsValveConfigurator.Mobile.ModifyConfigurator")}
            </MobileModifyTopLink>
            <SummaryCard>
                <SummaryHeading>{translate("FlsValveConfigurator.Bom.Summary")}</SummaryHeading>
                <SummaryGrid>
                    <SummaryCell>
                        <SummaryLabel>{translate("FlsValveConfigurator.Bom.ModelNo")}</SummaryLabel>
                        <SummaryValue>{r.modelNumber}</SummaryValue>
                    </SummaryCell>
                    <SummaryCell>
                        <SummaryLabel>{translate("FlsValveConfigurator.Bom.PartNo")}</SummaryLabel>
                        <SummaryValue>{r.partNumber}</SummaryValue>
                    </SummaryCell>
                    <SummaryCell>
                        <SummaryLabel>{translate("FlsValveConfigurator.Bom.PartsListNo")}</SummaryLabel>
                        <SummaryValue>{r.partsListNumber}</SummaryValue>
                    </SummaryCell>
                </SummaryGrid>
            </SummaryCard>

            {r.autolubricationNote?.trim() ? (
                <AutolubricationNote>
                    <strong>{translate("FlsValveConfigurator.Bom.Autolubrication")}</strong>
                    {r.autolubricationNote}
                </AutolubricationNote>
            ) : null}

            <UnitHeaderCard
                partNumber={r.partNumber}
                modelNumber={r.modelNumber}
                partsListNumber={r.partsListNumber}
                unitName={unitInfo?.name}
                unitPricePerAssembly={unitPricePerAssembly}
                unitPriceDisplay={unitTotalDisplay}
                bomLineCount={r.bom.length}
                unitQty={unitQty}
                onUnitQtyChange={setUnitQty}
                unitStock={unitStock}
                unitStockLabel={unitStockLabel}
                unitShippingCaption={unitShippingCaption}
                productDetailPath={unitInfo?.productDetailPath}
                isOrderable={isOrderable}
                onRequestQuote={scrollToQuoteForm}
                productContext={productContext}
                loading={pricing.loading || summary.loading}
            />

            <BomCard>
                <BomHeader>
                    <BomTitle>{translate("FlsValveConfigurator.Bom.Title")}</BomTitle>
                    <BomHeaderActions>
                        {pricing.loading ? (
                            <PricingStatus>
                                <Spinner />
                                {translate("FlsValveConfigurator.Bom.LoadingPrices")}
                            </PricingStatus>
                        ) : null}
                    </BomHeaderActions>
                </BomHeader>

                <ColumnLabels>
                    <ColPartLabel>{translate("FlsValveConfigurator.Bom.Part")}</ColPartLabel>
                    <ColStockLabel>{translate("FlsValveConfigurator.Bom.StockShipping")}</ColStockLabel>
                    <ColRightGroup>
                        <span>{translate("FlsValveConfigurator.Bom.Qty")}</span>
                        <span>{translate("FlsValveConfigurator.Bom.Unit")}</span>
                        <span>{translate("FlsValveConfigurator.Bom.Total")}</span>
                    </ColRightGroup>
                </ColumnLabels>

                <BomList>
                    {perLine.map(({ line, info, stock, shipDate }, i) => {
                        const flagged = line.qtoFlag || isContactInsideSales(line);
                        const unitDisplay = info ? info.unitNetPriceDisplay : "—";
                        const totalDisplay = info ? formatMoney(info.unitNetPrice * line.qty, currency) : "—";
                        const showSuggested =
                            !!info
                            && info.unitListPrice != null
                            && info.unitListPrice > info.unitNetPrice
                            && !!info.unitListPriceDisplay;
                        return (
                            <BomItem key={`${line.item}-${i}`}>
                                <Thumb aria-hidden="true">
                                    <img
                                        src={info?.imagePath || line.previewImageUrl || notFoundImage || ""}
                                        alt=""
                                    />
                                </Thumb>
                                <ItemMeta>
                                    <PartNumber>
                                        {translate("{0}:", translate("Part #"))}{" "}
                                        {info?.productDetailPath && line.partNumber ? (
                                            <PartNumberLink href={info.productDetailPath}>
                                                {line.partNumber}
                                            </PartNumberLink>
                                        ) : (
                                            line.partNumber || "—"
                                        )}
                                        {flagged ? <QtoBadge>QTO</QtoBadge> : null}
                                    </PartNumber>
                                    <Description>{line.description}</Description>
                                </ItemMeta>
                                <StockCell>
                                    {stock ? (
                                        <StockInlineRow>
                                            <StockInline
                                                _color={stockPresentation[stock.tone].color}
                                                _iconOffset={stockPresentation[stock.tone].iconOffset}
                                            >
                                                <Icon
                                                    color={stockPresentation[stock.tone].color}
                                                    src={stockPresentation[stock.tone].iconSrc}
                                                />
                                                <span>{stock.label}</span>
                                            </StockInline>
                                            <Tooltip
                                                {...tooltipProps}
                                                text={siteMessage("RealTimeInventory_AvailabilityTooltip").toString()}
                                            />
                                        </StockInlineRow>
                                    ) : null}
                                    {shipDate ? (
                                        <ShipDateLine>
                                            <span>{`Estimated shipping: ${shipDate}`}</span>
                                        </ShipDateLine>
                                    ) : null}
                                </StockCell>
                                <PriceCols>
                                    <PriceCol>
                                        <PriceLabel>{translate("FlsValveConfigurator.Bom.Qty")}</PriceLabel>
                                        <PriceValue>{line.qty}</PriceValue>
                                    </PriceCol>
                                    <PriceCol>
                                        <PriceLabel>{translate("FlsValveConfigurator.Bom.Unit")}</PriceLabel>
                                        <PriceValue $strong>{unitDisplay}</PriceValue>
                                        {showSuggested ? (
                                            <SuggestedPrice>{info!.unitListPriceDisplay}</SuggestedPrice>
                                        ) : null}
                                    </PriceCol>
                                    <PriceCol>
                                        <PriceLabel>{translate("FlsValveConfigurator.Bom.Total")}</PriceLabel>
                                        <PriceValue $strong>{totalDisplay}</PriceValue>
                                    </PriceCol>
                                </PriceCols>
                            </BomItem>
                        );
                    })}
                </BomList>

                <BomFooter>
                    <BomFooterLabel>{translate("FlsValveConfigurator.Bom.BomTotal")}</BomFooterLabel>
                    <BomFooterValue>{bomTotalDisplay}</BomFooterValue>
                </BomFooter>
            </BomCard>

            {!isOrderable ? (
                <QuoteFormSection ref={quoteFormRef} data-test-selector="valveQuoteFormSection">
                    <ValveConfiguratorQuoteForm
                        partNumber={r.partNumber}
                        modelNumber={r.modelNumber}
                        partsListNumber={r.partsListNumber}
                    />
                </QuoteFormSection>
            ) : null}

            <MobileStickyActions>
                <ModifyLinkButton type="button" onClick={onModify} data-test-selector="valveModifyMobile">
                    {translate("FlsValveConfigurator.Mobile.ModifyConfigurator")}
                </ModifyLinkButton>
            </MobileStickyActions>
            {/* Mounted here so the Add to List link inside the unit header card and the summary purchase card work even when the configurator page does not render the global <Modals /> component. */}
            <AddToListModal />
        </Stack>
    );
}

function QtoView({ r, onModify }: { r: ConfigureQtoModel; onModify: () => void }) {
    // QTO never queries inventory; the endpoint just returns the configured fallback weeks.
    const leadTime = useBomLeadTime({
        unitPartNumber: "",
        bomPartNumbers: [],
        isQuoteToOrder: true,
    });

    const quoteFormRef = useRef<HTMLDivElement>(null);

    const scrollToQuoteForm = () => {
        quoteFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Stack>
            <QtoPanel>
                <MobileModifyTopLink type="button" onClick={onModify} data-test-selector="valveModifyTopQto">
                    <span aria-hidden="true">←</span>
                    {translate("FlsValveConfigurator.Mobile.ModifyConfigurator")}
                </MobileModifyTopLink>
                <QtoHeader>
                    <span aria-hidden="true">⚠</span>
                    {translate("FlsValveConfigurator.Qto.Title")}
                </QtoHeader>
                <QtoMessage>{r.qtoMessage}</QtoMessage>
                {leadTime.caption ? <QtoLeadTime>{leadTime.caption}</QtoLeadTime> : null}
                <div>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={scrollToQuoteForm}
                        data-test-selector="valveRequestQuote"
                    >
                        {translate("FlsValveConfigurator.Qto.RequestQuote")} →
                    </Button>
                </div>
                <QtoFootnote>{translate("FlsValveConfigurator.Qto.ResponseTime")}</QtoFootnote>

                <MobileStickyActionsInPanel>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={scrollToQuoteForm}
                        data-test-selector="valveRequestQuoteMobile"
                    >
                        {translate("FlsValveConfigurator.Qto.RequestQuote")}
                    </Button>
                    <ModifyLinkButton type="button" onClick={onModify} data-test-selector="valveModifyMobileQto">
                        {translate("FlsValveConfigurator.Mobile.ModifyConfigurator")}
                    </ModifyLinkButton>
                </MobileStickyActionsInPanel>
            </QtoPanel>

            <QuoteFormSection ref={quoteFormRef} data-test-selector="valveQuoteFormSection">
                <ValveConfiguratorQuoteForm partNumber="" />
            </QuoteFormSection>
        </Stack>
    );
}

const ValveConfiguratorBomWidget: FC<Props> = ({ configurationResult, hasSubmitted }) => {
    const dispatch = useDispatch();

    if (!hasSubmitted || !configurationResult) {
        return null;
    }

    const r = configurationResult as ConfigureResponseModel;

    const onModify = () => {
        dispatch({ type: "Data/ValveConfigurator/SetMobileShowResults", show: false });
        if (typeof window !== "undefined") {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    return <Block>{r.isQto ? <QtoView r={r} onModify={onModify} /> : <SuccessView r={r} onModify={onModify} />}</Block>;
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ValveConfiguratorBomWidget),
    definition: {
        group: "Products",
        icon: "list",
        displayName: "Valve Configurator - Bill of Materials",
        fieldDefinitions: [],
    },
};

export default widgetModule;
